import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import Checkbox from "@mui/material/Checkbox";
import { motion, AnimatePresence } from "framer-motion";
import { Fragment, useEffect, useRef, useState } from "react";
import CommonButton from "../Buttons/CommonButton";
import LoadingSpinner from "../loadingspinner/loadingSpinner";
import NoDataFound from "../NoDataFound";

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

export default function EnhancedDynamicTablePagination({
  dataResult,
  setDataResult,
  fetchData,
  removeHeaders = [],
  tableClass = "",
  renderActions,
  renderCustomColumns = {},
  highlightRow = true,
  customRowBgColor = "#E9F5FE",
  customRowBorderColor = "#217E90",
  rowBackgroundColor,
  handleSelectedRow,
  editableColumns = [],
  renderInput,
  enableSelectAll = false,
  toggleSelectedRow = false,
  stickyActions = false,
  stickyColumns = [],
  stickyColumnWidths = {},
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  count,
  tableHeading,
  renderTableHeader,
  actionsPosition = "start",
  rowSize = "compact",
  showActionControls = false,
  onRowClick, // NEW: Single click handler
  onRowDoubleClick, // NEW: Double click handler
  rowHoverEffect = "highlight", // NEW: "highlight", "double-click", or "none"
  renderExpandedRow, // Optional: render a row below the selected row (pushes next record down)
  serverSidePagination = false, // When true, dataResult is current page only; fetchData called on page change
  stickyLeftColumns = [], // Columns to keep sticky on left during horizontal scroll (e.g. status, uhid, patientName)
  /** Data column keys to render after the checkbox and before the sticky Actions column (when actions at start). */
  columnsBeforeActions = [],
  /** Data column keys to render immediately after the sticky Actions column (when actions at start). */
  columnsAfterActions = [],
  tableCountContent, // Optional: counts/stats to show in table header between title and pagination
  scrollContainerRef, // Optional: ref for the internal scroll div (e.g. for parent-controlled auto-scroll)
  showPaginationBar = false, // When true, always shows the pagination bar (e.g. in modals with no table heading)
  /** When true with enableSelectAll, hides the blue "N of M records selected" bar */
  hideSelectionSummaryBar = false,
  /** Optional map of data key → column title (overrides camelCase split). */
  columnHeaderLabels = {},
  /** Optional content at the far left of the table toolbar (e.g. print icon above checkbox column). */
  headerStartContent = null,
  /** Stretch table to parent height (no fixed max-height). Use inside flex parents. */
  fillContainer = false,
}) {
  const formatHeaderLabel = (header) => {
    if (
      columnHeaderLabels &&
      Object.prototype.hasOwnProperty.call(columnHeaderLabels, header)
    ) {
      return columnHeaderLabels[header];
    }
    return header.replace(/([A-Z])/g, " $1").trim();
  };

  const [rowIndex, setRowIndex] = useState(null);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(null);
  const [isLoading] = useState(false);
  const [isStickyCollapsed, setIsStickyCollapsed] = useState(false);
  const [isActionsVisible, setIsActionsVisible] = useState(true);
  const [lastClickTime, setLastClickTime] = useState(0); // NEW: Track double-click
  const [lastClickedIndex, setLastClickedIndex] = useState(null); // NEW: Track last clicked row
  const [scrollContainerWidth, setScrollContainerWidth] = useState(null); // For expanded row sticky width

  const tableRef = useRef(null);

  // Measure scroll container width when expanded row is visible so expanded action bar can stay fixed
  useEffect(() => {
    if (rowIndex === null || !tableRef.current) {
      setScrollContainerWidth(null);
      return;
    }
    const el = tableRef.current;
    const updateWidth = () => setScrollContainerWidth(el.clientWidth);
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [rowIndex]);

  // ========== ROW SIZE CONFIGURATION ==========
  const getRowSizeClasses = () => {
    switch (rowSize) {
      case "compact":
        return {
          headerPadding: "px-3 py-2",
          cellPadding: "px-3 py-1.5",
          textSize: "text-xs",
          headerTextSize: "text-xs",
        };
      case "comfortable":
        return {
          headerPadding: "px-4 py-4",
          cellPadding: "px-4 py-4",
          textSize: "text-sm",
          headerTextSize: "text-xs",
        };
      default:
        return {
          headerPadding: "px-4 py-2.5",
          cellPadding: "px-4 py-2",
          textSize: "text-sm",
          headerTextSize: "text-xs",
        };
    }
  };

  const sizeClasses = getRowSizeClasses();

  // ========== INITIALIZE isChecked IF NOT EXISTS ==========
  useEffect(() => {
    if (dataResult && dataResult.length > 0) {
      const needsInit = dataResult.some((item) => !("isChecked" in item));
      if (needsInit) {
        const initializedData = dataResult.map((item) => ({
          ...item,
          isChecked: item.isChecked || false,
        }));
        console.log("Initializing isChecked property:", initializedData);
        setDataResult(initializedData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========== SELECTION STATS ==========
  const getSelectionStats = () => {
    const selectedCount = dataResult.filter((row) => row.isChecked).length;
    const totalOnPage = dataResult.length;
    const startRecord = page * rowsPerPage + 1;
    const endRecord = Math.min((page + 1) * rowsPerPage, count);

    console.log("=== SELECTION STATS ===");
    console.log("Selected count:", selectedCount);
    console.log("Total on page:", totalOnPage);
    console.log("dataResult length:", dataResult.length);
    console.log("count (total):", count);

    return {
      selectedCount,
      totalOnPage,
      startRecord,
      endRecord,
      isAllSelected: selectedCount === totalOnPage && totalOnPage > 0,
      isPartiallySelected: selectedCount > 0 && selectedCount < totalOnPage,
    };
  };

  const selectionStats = getSelectionStats();

  // ========== GET PAGINATED DATA FOR DISPLAY ==========
  const getPaginatedData = () => {
    if (serverSidePagination) {
      // Server returns current page only; show as-is
      return dataResult || [];
    }
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    console.log("=== GET PAGINATED DATA ===");
    console.log("Page:", page);
    console.log("RowsPerPage:", rowsPerPage);
    console.log("StartIndex:", startIndex);
    console.log("EndIndex:", endIndex);
    console.log("DataResult length:", dataResult.length);

    const paginated = dataResult.slice(startIndex, endIndex);
    console.log("Paginated data:", paginated);
    return paginated;
  };

  const paginatedData = getPaginatedData();

  // ========== HEADERS LOGIC ==========
  const headers = Object.keys(paginatedData[0] || dataResult[0] || {}).filter(
    (header) => {
      const lowerHeader = header.toLowerCase();
      const alwaysRemove = ["id", "ischecked"];
      return !(
        removeHeaders.includes(header) || alwaysRemove.includes(lowerHeader)
      );
    }
  );

  const effectiveBeforeActions = columnsBeforeActions.filter((h) =>
    headers.includes(h),
  );
  const effectiveAfterActions = columnsAfterActions.filter((h) =>
    headers.includes(h),
  );
  const mainHeaders = headers.filter(
    (h) =>
      !effectiveBeforeActions.includes(h) &&
      !effectiveAfterActions.includes(h),
  );

  const scrollHeaders = mainHeaders.filter(
    (h) => !stickyColumns.includes(h) && !stickyLeftColumns.includes(h)
  );
  const nonStickyHeaders = headers.filter((h) => !stickyColumns.includes(h));

  const totalColSpan =
    (enableSelectAll ? 1 : 0) +
    (isActionsVisible && renderActions && stickyActions ? 1 : 0) +
    headers.length +
    (!isStickyCollapsed ? stickyColumns.length : 0);

  // ========== COLUMN WIDTHS ==========
  const getColumnWidths = () => {
    const widths = {};
    widths.checkbox = "40px";
    widths.actions = actionsPosition === "start" ? "100px" : "120px";
    headers.forEach((header) => {
      if (stickyColumnWidths[header]) {
        widths[header] = stickyColumnWidths[header];
        return;
      }
      const headerLength = header.length;
      const maxDataLength = (
        paginatedData.length > 0 ? paginatedData : dataResult
      ).reduce((max, row) => {
        const value = String(
          row[header] === true
            ? "Yes"
            : row[header] === false
            ? "No"
            : row[header] || ""
        );
        return Math.max(max, value.length);
      }, 0);
      const baseWidth = Math.max(headerLength, maxDataLength) * 8 + 32;
      widths[header] = `${Math.min(baseWidth, 200)}px`;
    });
    return widths;
  };

  const computedWidths = getColumnWidths();

  const pxWidth = (w) => parseInt(String(w || 0), 10) || 0;

  const getBeforeActionsStickyLeft = (columnIndex) => {
    let offset = enableSelectAll ? pxWidth(computedWidths.checkbox) : 0;
    for (let i = 0; i < columnIndex; i++) {
      const key = effectiveBeforeActions[i];
      offset += pxWidth(computedWidths[key]);
    }
    return offset;
  };

  const getActionsStickyLeftPx = () => {
    let offset = enableSelectAll ? pxWidth(computedWidths.checkbox) : 0;
    effectiveBeforeActions.forEach((h) => {
      offset += pxWidth(computedWidths[h]);
    });
    return offset;
  };

  const getAfterActionsStickyLeft = (columnIndex) => {
    let offset = enableSelectAll ? pxWidth(computedWidths.checkbox) : 0;
    effectiveBeforeActions.forEach((h) => {
      offset += pxWidth(computedWidths[h]);
    });
    if (
      isActionsVisible &&
      renderActions &&
      stickyActions &&
      actionsPosition === "start"
    ) {
      offset += pxWidth(computedWidths.actions);
    }
    for (let i = 0; i < columnIndex; i++) {
      const key = effectiveAfterActions[i];
      offset += pxWidth(computedWidths[key]);
    }
    return offset;
  };

  const getStickyLeftOffset = (header) => {
    const idx = stickyLeftColumns.indexOf(header);
    if (idx < 0) return 0;
    let offset = enableSelectAll ? pxWidth(computedWidths.checkbox) : 0;
    effectiveBeforeActions.forEach((h) => {
      offset += pxWidth(computedWidths[h]);
    });
    if (
      isActionsVisible &&
      renderActions &&
      stickyActions &&
      actionsPosition === "start"
    )
      offset += pxWidth(computedWidths.actions);
    effectiveAfterActions.forEach((h) => {
      offset += pxWidth(computedWidths[h]);
    });
    for (let i = 0; i < idx; i++) {
      offset += pxWidth(computedWidths[stickyLeftColumns[i]]);
    }
    return offset;
  };

  // ========== STICKY COLUMN RIGHT POSITION ==========
  const getStickyColumnRight = (index) => {
    let rightOffset = 0;
    if (actionsPosition === "end" && isActionsVisible && stickyActions) {
      rightOffset = parseInt(computedWidths.actions);
    }
    if (!isStickyCollapsed) {
      for (let i = index + 1; i < stickyColumns.length; i++) {
        const col = stickyColumns[i];
        rightOffset += parseInt(computedWidths[col] || 0);
      }
    }
    return `${rightOffset}px`;
  };

  // ========== SORT HANDLER ==========
  const createSortHandler = (property) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // ========== SELECT ALL - UPDATES ALL IN dataResult ==========
  const handleSelectAll = () => {
    const allChecked = paginatedData.every((row) => row.isChecked);

    console.log("=== SELECT ALL DEBUG ===");
    console.log("Paginated data length:", paginatedData.length);
    console.log("All checked:", allChecked);
    console.log("Total dataResult length:", dataResult.length);

    // Update ALL data in dataResult, not just paginated
    const updatedDataResult = dataResult.map((row) => ({
      ...row,
      isChecked: !allChecked,
    }));

    console.log("Updated all dataResult:", updatedDataResult);
    setDataResult(updatedDataResult);
  };

  // ========== ROW SELECT - UPDATES ENTIRE dataResult ==========
  const handleRowSelect = (index) => {
    console.log("=== ROW SELECT DEBUG ===");
    console.log("Clicked row index on paginated view:", index);
    console.log("Page:", page);
    console.log("Rows per page:", rowsPerPage);

    // Find the absolute index in dataResult
    const absoluteIndex = page * rowsPerPage + index;
    console.log("Absolute index in dataResult:", absoluteIndex);

    // Update entire dataResult
    const updatedDataResult = dataResult.map((row, idx) =>
      idx === absoluteIndex ? { ...row, isChecked: !row.isChecked } : row
    );

    console.log("Updated dataResult:", updatedDataResult);
    setDataResult(updatedDataResult);
  };

  // ========== PAGE CHANGE HANDLER ==========
  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil((count || 0) / rowsPerPage);
    console.log("=== PAGE CHANGE ===");
    console.log("New page:", newPage);
    console.log("Max page:", maxPage);

    if (newPage < 0 || newPage >= maxPage) return;
    // Server-side: fetch next/prev page then update page state
    if (serverSidePagination && fetchData) {
      fetchData({ page: newPage, rowsPerPage });
    }
    setPage(newPage);
  };

  // ========== NEW: DOUBLE-CLICK HANDLER ==========
  const handleRowClickWithDoubleClick = (row, index) => {
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - lastClickTime;

    console.log("=== ROW CLICK DEBUG ===");
    console.log("Current click time:", currentTime);
    console.log("Last click time:", lastClickTime);
    console.log("Time difference:", timeDiff);
    console.log("Is double-click:", timeDiff < 300);

    // Check if it's a double-click (within 300ms)
    if (timeDiff < 300 && lastClickedIndex === index) {
      console.log("🔴 DOUBLE-CLICK DETECTED on row:", row);
      setLastClickTime(0);
      setLastClickedIndex(null);

      // Call double-click handler
      if (onRowDoubleClick) {
        onRowDoubleClick(row, index);
      }
    } else {
      // Single click
      console.log("🟢 SINGLE-CLICK on row:", row);
      setLastClickTime(currentTime);
      setLastClickedIndex(index);

      // Call single-click handler
      if (onRowClick) {
        onRowClick(row, index);
      }

      // Original row highlight behavior
      const selectedIndex =
        toggleSelectedRow && rowIndex === index ? null : index;
      setRowIndex(selectedIndex);
      handleSelectedRow?.(selectedIndex !== null ? row : null, index);
    }
  };

  // ========== KEYBOARD NAVIGATION ==========
  const handleKeyDown = (event, tIndex) => {
    if (highlightRow && rowIndex !== null) {
      if (event.key === "ArrowUp" && rowIndex > 0) {
        const newIndex = rowIndex - 1;
        setRowIndex(newIndex);
        handleSelectedRow?.(paginatedData[newIndex], newIndex);
        event.preventDefault();
      } else if (
        event.key === "ArrowDown" &&
        rowIndex < paginatedData.length - 1
      ) {
        const newIndex = rowIndex + 1;
        setRowIndex(newIndex);
        handleSelectedRow?.(paginatedData[newIndex], newIndex);
        event.preventDefault();
      } else if (event.key === "Enter") {
        handleSelectedRow?.(paginatedData[rowIndex], rowIndex);
        event.preventDefault();
      }
    }
  };

  useEffect(() => {
    const table = tableRef.current;
    if (table) {
      table.addEventListener("keydown", handleKeyDown);
      return () => table.removeEventListener("keydown", handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowIndex, paginatedData, page, count]);

  // ========== RENDER ACTIONS COLUMN ==========
  const renderActionsColumn = (row, index, isHeader = false) => {
    if (isHeader) {
      return (
        <th
          className={`${sizeClasses.headerPadding} text-left ${
            sizeClasses.headerTextSize
          } font-bold text-gray-800 uppercase tracking-wider ${
            actionsPosition === "start"
              ? `bg-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]`
              : "right-0 bg-gray-200 shadow-md"
          }`}
          style={{
            position: "sticky",
            zIndex: 50,
            width: computedWidths.actions,
            minWidth: computedWidths.actions,
            ...(actionsPosition === "start"
              ? { left: `${getActionsStickyLeftPx()}px` }
              : { right: 0 }),
          }}
        >
          Actions
        </th>
      );
    }

    const cellBg =
      rowIndex === index && highlightRow
        ? customRowBgColor
        : rowBackgroundColor?.(row, index) || "#fff";
    return (
      <td
        className={`${sizeClasses.cellPadding} ${
          sizeClasses.textSize
        } font-medium ${
          actionsPosition === "start" ? `shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]` : "shadow-md"
        }`}
        style={{
          position: "sticky",
          zIndex: 30,
          width: computedWidths.actions,
          minWidth: computedWidths.actions,
          background: cellBg,
          ...(actionsPosition === "start"
            ? { left: `${getActionsStickyLeftPx()}px` }
            : { right: 0 }),
        }}
      >
        <div className="flex items-center space-x-1">
          {renderActions(row, index)}
        </div>
      </td>
    );
  };

  // ========== RENDER ==========
  return (
    <div
      className={`w-full bg-white rounded-lg border border-gray-200 overflow-hidden ${
        fillContainer ? "h-full flex flex-col min-h-0" : ""
      }`}
    >
      {/* Table Header Section */}
      {(tableHeading ||
        headerStartContent ||
        (renderTableHeader && renderTableHeader()) ||
        showActionControls ||
        showPaginationBar) && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 py-1 bg-gray-50 border-b border-gray-200 shrink-0">
          {/* Left section - leading toolbar, title, counts, and custom header */}
          <div className="flex items-start sm:items-center gap-3 flex-1 min-w-0">
            {headerStartContent && (
              <div className="flex items-center shrink-0 pt-0.5">{headerStartContent}</div>
            )}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 flex-1 min-w-0">
              {tableHeading && (
                <h2 className="text-base font-semibold text-gray-900 truncate">
                  {tableHeading}
                </h2>
              )}
              {tableCountContent && (
                <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-700 flex-wrap">
                  {tableCountContent}
                </div>
              )}
              {renderTableHeader && renderTableHeader()}
            </div>
          </div>

          {/* Right section - Controls and Pagination */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
            {showActionControls && (
              <div className="flex items-center gap-2">
                {stickyColumns.length > 0 && (
                  <CommonButton
                    onClick={() => setIsStickyCollapsed(!isStickyCollapsed)}
                    icon={
                      isStickyCollapsed ? (
                        <EyeIcon className="h-4 w-4" />
                      ) : (
                        <EyeSlashIcon className="h-4 w-4" />
                      )
                    }
                    label={isStickyCollapsed ? "Show Columns" : "Hide Columns"}
                    showLabel={false}
                    tooltipPosition="bottom"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900 bg-white border border-gray-300 hover:border-gray-400 rounded-md p-2 transition-colors"
                  />
                )}
                {renderActions && (
                  <CommonButton
                    onClick={() => setIsActionsVisible(!isActionsVisible)}
                    icon={
                      isActionsVisible ? (
                        <EyeSlashIcon className="h-4 w-4" />
                      ) : (
                        <EyeIcon className="h-4 w-4" />
                      )
                    }
                    label={isActionsVisible ? "Hide Actions" : "Show Actions"}
                    showLabel={false}
                    tooltipPosition="bottom"
                    size="sm"
                    className="text-gray-600 hover:text-gray-900 bg-white border border-gray-300 hover:border-gray-400 rounded-md p-2 transition-colors"
                  />
                )}
              </div>
            )}

            {/* Pagination Controls */}
            <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-full shadow-sm border border-gray-200">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0 || isLoading}
                className={`p-1 rounded-full ${
                  page === 0 || isLoading
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                } transition-colors`}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium text-gray-700 px-2 min-w-[50px] text-center">
                {page + 1} / {Math.ceil(count / rowsPerPage)}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={
                  page >= Math.ceil(count / rowsPerPage) - 1 || isLoading
                }
                className={`p-1 rounded-full ${
                  page >= Math.ceil(count / rowsPerPage) - 1 || isLoading
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-600 hover:bg-gray-100 hover:text-blue-600"
                } transition-colors`}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Status Message */}
      {enableSelectAll &&
        !hideSelectionSummaryBar &&
        selectionStats.selectedCount > 0 && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-200">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm text-blue-700">
              <strong>{selectionStats.selectedCount}</strong> of{" "}
              <strong>{count}</strong> records selected
            </span>
            <button
              onClick={() => {
                const updatedDataResult = dataResult.map((row) => ({
                  ...row,
                  isChecked: false,
                }));
                setDataResult(updatedDataResult);
              }}
              className="text-xs text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Table Content */}
      {!!paginatedData && paginatedData.length > 0 ? (
        <div
          ref={(el) => {
            tableRef.current = el;
            if (scrollContainerRef) scrollContainerRef.current = el;
          }}
          tabIndex="0"
          className={
            fillContainer
              ? `hims-table-scroll relative flex-1 min-h-0 overflow-auto outline-none ${tableClass}`
              : `hims-table-scroll relative max-h-[calc(100vh-14rem)] lg:max-h-[600px] overflow-auto outline-none ${tableClass}`
          }
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "#CBD5E1 #F1F5F9",
          }}
        >
          <table className="w-full min-w-max">
            {/* Table Header */}
            <thead className="bg-gray-200 sticky top-0 z-40">
              <tr className="border-b border-gray-200">
                {enableSelectAll && (
                  <th
                    className={`${sizeClasses.headerPadding} text-left ${sizeClasses.headerTextSize} font-bold text-gray-800 uppercase tracking-wider sticky left-0 bg-gray-200 z-50`}
                    style={{ width: computedWidths.checkbox }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectionStats.isAllSelected}
                      indeterminate={selectionStats.isPartiallySelected}
                      onChange={handleSelectAll}
                      sx={{
                        color: "#9CA3AF",
                        "&.Mui-checked": { color: "#3B82F6" },
                        "&.MuiCheckbox-indeterminate": { color: "#3B82F6" },
                        margin: 0,
                        padding: 0,
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.25rem",
                        },
                      }}
                    />
                  </th>
                )}

                {effectiveBeforeActions.map((header, beforeIdx) => (
                  <th
                    key={`before-actions-${header}`}
                    className={`${sizeClasses.headerPadding} text-left ${sizeClasses.headerTextSize} font-bold text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors no-wrap sticky bg-gray-200 z-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]`}
                    style={{
                      width: computedWidths[header],
                      left: `${getBeforeActionsStickyLeft(beforeIdx)}px`,
                    }}
                    onClick={createSortHandler(header)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{formatHeaderLabel(header)}</span>
                      {orderBy === header && (
                        <span
                          className={`text-gray-400 transition-transform ${
                            order === "desc" ? "rotate-180" : ""
                          }`}
                        >
                          ↑
                        </span>
                      )}
                    </div>
                  </th>
                ))}

                {isActionsVisible &&
                  renderActions &&
                  stickyActions &&
                  actionsPosition === "start" &&
                  renderActionsColumn(null, null, true)}

                {actionsPosition === "start" &&
                  effectiveAfterActions.map((header, afterIdx) => (
                    <th
                      key={`after-actions-${header}`}
                      className={`${sizeClasses.headerPadding} text-left ${sizeClasses.headerTextSize} font-bold text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors no-wrap sticky bg-gray-200 z-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]`}
                      style={{
                        width: computedWidths[header],
                        left: `${getAfterActionsStickyLeft(afterIdx)}px`,
                      }}
                      onClick={createSortHandler(header)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{formatHeaderLabel(header)}</span>
                        {orderBy === header && (
                          <span
                            className={`text-gray-400 transition-transform ${
                              order === "desc" ? "rotate-180" : ""
                            }`}
                          >
                            ↑
                          </span>
                        )}
                      </div>
                    </th>
                  ))}

                {stickyLeftColumns
                  .filter((h) => mainHeaders.includes(h))
                  .map((header) => (
                    <th
                      key={header}
                      className={`${sizeClasses.headerPadding} text-left ${sizeClasses.headerTextSize} font-bold text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors no-wrap sticky bg-gray-200 z-50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]`}
                      style={{
                        width: computedWidths[header],
                        left: `${getStickyLeftOffset(header)}px`,
                      }}
                      onClick={createSortHandler(header)}
                    >
                      <div className="flex items-center space-x-1">
                        <span>{formatHeaderLabel(header)}</span>
                        {orderBy === header && (
                          <span
                            className={`text-gray-400 transition-transform ${
                              order === "desc" ? "rotate-180" : ""
                            }`}
                          >
                            ↑
                          </span>
                        )}
                      </div>
                    </th>
                  ))}

                {scrollHeaders.map((header) => (
                  <th
                    key={header}
                    className={`${sizeClasses.headerPadding} text-left ${sizeClasses.headerTextSize} font-bold text-gray-800 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors no-wrap`}
                    style={{ width: computedWidths[header] }}
                    onClick={createSortHandler(header)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{formatHeaderLabel(header)}</span>
                      {orderBy === header && (
                        <span
                          className={`text-gray-400 transition-transform ${
                            order === "desc" ? "rotate-180" : ""
                          }`}
                        >
                          ↑
                        </span>
                      )}
                    </div>
                  </th>
                ))}

                {!isStickyCollapsed &&
                  stickyColumns.map((header, idx) => (
                    <th
                      key={header}
                      className={`${sizeClasses.headerPadding} text-left ${sizeClasses.headerTextSize} font-bold text-gray-800 uppercase tracking-wider sticky bg-gray-200 z-50 shadow-md`}
                      style={{
                        width: computedWidths[header],
                        right: getStickyColumnRight(idx),
                      }}
                    >
                      {formatHeaderLabel(header)}
                    </th>
                  ))}

                {isActionsVisible &&
                  renderActions &&
                  stickyActions &&
                  actionsPosition === "end" &&
                  renderActionsColumn(null, null, true)}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={
                      nonStickyHeaders.length +
                      (enableSelectAll ? 1 : 0) +
                      (!isStickyCollapsed ? stickyColumns.length : 0) +
                      (isActionsVisible && stickyActions ? 1 : 0)
                    }
                    className="p-8 text-center"
                  >
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : (
                stableSort(paginatedData, getComparator(order, orderBy)).map(
                  (row, index) => (
                    <Fragment key={index}>
                      <tr
                        className={`${
                          rowHoverEffect !== "none"
                            ? "hover:bg-gray-50 cursor-pointer"
                            : ""
                        } transition-colors`}
                        style={
                          rowIndex === index && highlightRow
                            ? {
                                boxShadow: `inset 0 0 0 1px ${customRowBorderColor}`,
                              }
                            : undefined
                        }
                        onClick={() => handleRowClickWithDoubleClick(row, index)}
                      >
                        {enableSelectAll && (
                          <td
                            className={`${sizeClasses.cellPadding} sticky left-0 z-30`}
                            style={{
                              width: computedWidths.checkbox,
                              background:
                                rowIndex === index && highlightRow
                                  ? customRowBgColor
                                  : rowBackgroundColor?.(row, index) || "#fff",
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={row.isChecked || false}
                              onChange={() => handleRowSelect(index)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                color: "#9CA3AF",
                                "&.Mui-checked": { color: "#3B82F6" },
                                margin: 0,
                                padding: 0,
                                "& .MuiSvgIcon-root": {
                                  fontSize: "1.25rem",
                                },
                              }}
                            />
                          </td>
                        )}

                        {effectiveBeforeActions.map((header, beforeIdx) => (
                          <td
                            key={`before-actions-${header}-${beforeIdx}`}
                            className={`${sizeClasses.cellPadding} ${sizeClasses.textSize} text-gray-900 sticky z-30 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]`}
                            style={{
                              width: computedWidths[header],
                              left: `${getBeforeActionsStickyLeft(beforeIdx)}px`,
                              background:
                                rowIndex === index && highlightRow
                                  ? customRowBgColor
                                  : rowBackgroundColor?.(row, index) || "#fff",
                            }}
                          >
                            <div className="truncate">
                              {renderCustomColumns[header] ? (
                                renderCustomColumns[header](row, index)
                              ) : editableColumns.includes(header) &&
                                renderInput ? (
                                renderInput(row, index, header)
                              ) : row[header] === true ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Yes
                                </span>
                              ) : row[header] === false ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  No
                                </span>
                              ) : (
                                row[header] || (
                                  <span className="text-gray-400">-</span>
                                )
                              )}
                            </div>
                          </td>
                        ))}

                        {isActionsVisible &&
                          renderActions &&
                          stickyActions &&
                          actionsPosition === "start" &&
                          renderActionsColumn(row, index)}

                        {actionsPosition === "start" &&
                          effectiveAfterActions.map((header, afterIdx) => (
                            <td
                              key={`after-actions-${header}-${afterIdx}`}
                              className={`${sizeClasses.cellPadding} ${sizeClasses.textSize} text-gray-900 sticky z-30 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]`}
                              style={{
                                width: computedWidths[header],
                                left: `${getAfterActionsStickyLeft(afterIdx)}px`,
                                background:
                                  rowIndex === index && highlightRow
                                    ? customRowBgColor
                                    : rowBackgroundColor?.(row, index) ||
                                      "#fff",
                              }}
                            >
                              <div className="truncate">
                                {renderCustomColumns[header] ? (
                                  renderCustomColumns[header](row, index)
                                ) : editableColumns.includes(header) &&
                                  renderInput ? (
                                  renderInput(row, index, header)
                                ) : row[header] === true ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Yes
                                  </span>
                                ) : row[header] === false ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    No
                                  </span>
                                ) : (
                                  row[header] || (
                                    <span className="text-gray-400">-</span>
                                  )
                                )}
                              </div>
                            </td>
                          ))}

                        {stickyLeftColumns
                          .filter((h) => mainHeaders.includes(h))
                          .map((header) => (
                            <td
                              key={header}
                              className={`${sizeClasses.cellPadding} ${sizeClasses.textSize} text-gray-900 sticky z-30 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]`}
                              style={{
                                width: computedWidths[header],
                                left: `${getStickyLeftOffset(header)}px`,
                                background:
                                  rowIndex === index && highlightRow
                                    ? customRowBgColor
                                    : rowBackgroundColor?.(row, index) || "#fff",
                              }}
                            >
                              <div className="truncate">
                                {renderCustomColumns[header] ? (
                                  renderCustomColumns[header](row, index)
                                ) : editableColumns.includes(header) &&
                                  renderInput ? (
                                  renderInput(row, index, header)
                                ) : row[header] === true ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Yes
                                  </span>
                                ) : row[header] === false ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    No
                                  </span>
                                ) : (
                                  row[header] || (
                                    <span className="text-gray-400">-</span>
                                  )
                                )}
                              </div>
                            </td>
                          ))}

                        {scrollHeaders.map((header) => (
                          <td
                            key={header}
                            className={`${sizeClasses.cellPadding} ${sizeClasses.textSize} text-gray-900`}
                            style={{
                              width: computedWidths[header],
                              background:
                                rowIndex === index && highlightRow
                                  ? customRowBgColor
                                  : rowBackgroundColor?.(row, index) || "#fff",
                            }}
                          >
                            <div className="truncate">
                              {renderCustomColumns[header] ? (
                                renderCustomColumns[header](row, index)
                              ) : editableColumns.includes(header) &&
                                renderInput ? (
                                renderInput(row, index, header)
                              ) : row[header] === true ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Yes
                                </span>
                              ) : row[header] === false ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                  No
                                </span>
                              ) : (
                                row[header] || (
                                  <span className="text-gray-400">-</span>
                                )
                              )}
                            </div>
                          </td>
                        ))}

                        {!isStickyCollapsed &&
                          stickyColumns.map((header, idx) => (
                            <td
                              key={header}
                              className={`${sizeClasses.cellPadding} ${sizeClasses.textSize} text-gray-800 sticky z-30 shadow-md`}
                              style={{
                                width: computedWidths[header],
                                right: getStickyColumnRight(idx),
                                background:
                                  rowIndex === index && highlightRow
                                    ? customRowBgColor
                                    : rowBackgroundColor?.(row, index) || "#fff",
                              }}
                            >
                              <div className="truncate">
                                {renderCustomColumns[header] ? (
                                  renderCustomColumns[header](row, index)
                                ) : editableColumns.includes(header) &&
                                  renderInput ? (
                                  renderInput(row, index, header)
                                ) : row[header] === true ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Yes
                                  </span>
                                ) : row[header] === false ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    No
                                  </span>
                                ) : (
                                  row[header] || (
                                    <span className="text-gray-400">-</span>
                                  )
                                )}
                              </div>
                            </td>
                          ))}

                        {isActionsVisible &&
                          renderActions &&
                          stickyActions &&
                          actionsPosition === "end" &&
                          renderActionsColumn(row, index)}
                      </tr>
                      {renderExpandedRow && (
                        <AnimatePresence>
                          {rowIndex === index && (
                            <tr
                              key={`expanded-${index}`}
                              className="bg-gray-50 border-b border-gray-200"
                            >
                              <td
                                colSpan={totalColSpan}
                                className="px-0 py-0 align-top border-none p-0"
                                style={{ verticalAlign: "top", padding: 0 }}
                              >
                                <div
                                  className="px-3 py-2 bg-gray-50 z-20"
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                    boxShadow: "0 -2px 4px rgba(0,0,0,0.05)",
                                    width: scrollContainerWidth != null ? `${scrollContainerWidth}px` : "100%",
                                    minWidth: scrollContainerWidth != null ? `${scrollContainerWidth}px` : undefined,
                                    boxSizing: "border-box",
                                  }}
                                >
                                  <motion.div
                                    initial={{ opacity: 0, y: -12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{
                                      duration: 0.3,
                                      ease: [0.25, 0.46, 0.45, 0.94],
                                    }}
                                  >
                                    {renderExpandedRow(row, index)}
                                  </motion.div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </AnimatePresence>
                      )}
                    </Fragment>
                  )
                )
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-12">
          <NoDataFound className={`text-center ${tableClass}`} />
        </div>
      )}
    </div>
  );
}
